import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  FormGroup,
  Label,
  Alert,
} from "reactstrap";
import { PostData } from "../../services/api";
import Swal from "sweetalert2";

const CNAME_TARGET = process.env.REACT_APP_LANDING_CNAME || "landings.negocioeradigital.com";

const toSlug = (name) =>
  (name || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60) || "landing";

const PublishModal = ({ isOpen, toggle, page, onPublished }) => {
  const [tab, setTab] = useState("free");
  const [customDomain, setCustomDomain] = useState("");
  const [dnsOk, setDnsOk] = useState(false);
  const [dnsMsg, setDnsMsg] = useState("");
  const [checking, setChecking] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);

  // Slug editable para subdominio gratuito
  const [slug, setSlug] = useState("");
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugOk, setSlugOk] = useState(false);
  const [slugMsg, setSlugMsg] = useState("");

  const isPublished = !!page?.isPublished;
  const defaultDomain = process.env.REACT_APP_LANDING_DOMAIN || "negocioeradigital.com";
  const freeUrl = `https://${slug || "landing"}.${defaultDomain}`;

  useEffect(() => {
    if (!isOpen) return;
    setDnsOk(false);
    setDnsMsg("");
    setChecking(false);
    setCustomDomain(page?.customDomain || "");
    const initialSlug = page?.slug || toSlug(page?.name);
    setSlug(initialSlug);
    setSlugOk(true); // slug actual del page siempre es válido
    setSlugMsg("");
    if (page?.customDomain) setTab("custom");
    else setTab("free");
  }, [isOpen, page]);

  // Debounced slug validation — revalida al cambiar
  useEffect(() => {
    if (!isOpen || tab !== "free" || !slug) return;
    // El slug actual del page no requiere validación
    if (slug === (page?.slug || toSlug(page?.name))) {
      setSlugOk(true);
      setSlugMsg("");
      return;
    }
    setSlugOk(false);
    setSlugMsg("");
    setSlugChecking(true);
    const t = setTimeout(async () => {
      try {
        const res = await PostData(`/pages/${page.id}/check-slug`, { slug });
        setSlugOk(res?.data?.available || false);
        setSlugMsg(res?.data?.message || "");
        // Si el backend normalizó el slug (quitó acentos, etc.), reflejarlo
        if (res?.data?.slug && res.data.slug !== slug) setSlug(res.data.slug);
      } catch (err) {
        setSlugOk(false);
        setSlugMsg(err.message || "Error al verificar el nombre");
      } finally {
        setSlugChecking(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [slug, isOpen, tab, page]);

  const handleCheckDns = async () => {
    if (!customDomain.trim()) return;
    setChecking(true);
    setDnsMsg("");
    try {
      const res = await PostData(`/pages/${page.id}/check-domain`, { domain: customDomain.trim() });
      setDnsOk(res?.data?.ok || false);
      setDnsMsg(res?.data?.message || "");
    } catch (err) {
      setDnsOk(false);
      setDnsMsg(err.message || "Error al verificar DNS");
    } finally {
      setChecking(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const body =
        tab === "custom"
          ? { customDomain: customDomain.trim() }
          : { slug: slug.trim() };
      const res = await PostData(`/pages/${page.id}/publish`, body);
      const url = res?.data?.url || "";
      Swal.fire({
        icon: "success",
        title: "Publicada",
        html: url
          ? `Tu landing est\u00e1 en:<br/><a href="${url}" target="_blank" rel="noopener" style="word-break:break-all;font-size:14px;font-weight:600;">${url}</a>`
          : res?.data?.message || "Publicada correctamente.",
      });
      onPublished?.();
      toggle();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message || "No se pudo publicar" });
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    const ok = await Swal.fire({
      title: "\u00bfDespublicar esta p\u00e1gina?",
      text: "Dejar\u00e1 de ser accesible p\u00fablicamente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E9553E",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "S\u00ed, despublicar",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setUnpublishing(true);
    try {
      await PostData(`/pages/${page.id}/unpublish`, {});
      Swal.fire({ icon: "success", title: "Despublicada", timer: 1500, showConfirmButton: false });
      onPublished?.();
      toggle();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message || "No se pudo despublicar" });
    } finally {
      setUnpublishing(false);
    }
  };

  const canPublishFree = !publishing;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <i className="mdi mdi-earth me-2" />
        {isPublished ? "Administrar publicaci\u00f3n" : "Publicar p\u00e1gina"}
      </ModalHeader>

      <ModalBody>
        {/* ── Published state ── */}
        {isPublished && (
          <div className="mb-4 p-3 rounded" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <i className="mdi mdi-check-circle text-success" style={{ fontSize: 20 }} />
              <strong className="text-success">P\u00e1gina publicada</strong>
            </div>
            <a
              href={page.publishedUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 14, wordBreak: "break-all", fontWeight: 600 }}
            >
              {page.publishedUrl}
            </a>
            <div className="mt-3 d-flex gap-2 flex-wrap">
              <Button
                size="sm"
                color="primary"
                outline
                onClick={() => {
                  navigator.clipboard.writeText(page.publishedUrl || "");
                  Swal.fire({ icon: "success", title: "URL copiada", timer: 800, showConfirmButton: false });
                }}
              >
                <i className="mdi mdi-content-copy me-1" />Copiar URL
              </Button>
              <Button size="sm" color="primary" outline onClick={() => window.open(page.publishedUrl, "_blank")}>
                <i className="mdi mdi-open-in-new me-1" />Abrir
              </Button>
              <Button size="sm" color="danger" outline onClick={handleUnpublish} disabled={unpublishing}>
                {unpublishing ? (
                  <><span className="spinner-border spinner-border-sm me-1" style={{ width: ".7rem", height: ".7rem" }} />Despublicando...</>
                ) : (
                  <><i className="mdi mdi-earth-off me-1" />Despublicar</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ── Publish options (or re-publish) ── */}
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink className={tab === "free" ? "active" : ""} onClick={() => setTab("free")} style={{ cursor: "pointer" }}>
              <i className="mdi mdi-web me-1" />Subdominio gratuito
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink className={tab === "custom" ? "active" : ""} onClick={() => { setTab("custom"); setDnsOk(false); setDnsMsg(""); }} style={{ cursor: "pointer" }}>
              <i className="mdi mdi-dns me-1" />Dominio propio
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={tab}>
          {/* ── Tab: Free subdomain ── */}
          <TabPane tabId="free">
            <div className="mb-3">
              <Label className="small fw-semibold text-muted mb-1">
                Elige el nombre de tu subdominio
              </Label>
              <div
                className="d-flex align-items-stretch"
                style={{
                  border: `1px solid ${slugOk ? "#28a745" : slug && !slugChecking && slugMsg ? "#dc3545" : "#ced4da"}`,
                  borderRadius: 6,
                  overflow: "hidden",
                  background: "#fff",
                  fontFamily: "monospace",
                  fontSize: 14,
                }}
              >
                <span style={{ padding: "10px 12px", background: "#f8f9fa", color: "#666", borderRight: "1px solid #e9ecef", userSelect: "none" }}>
                  https://
                </span>
                <Input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(toSlug(e.target.value))}
                  placeholder="mi-landing"
                  className="border-0"
                  style={{ fontFamily: "monospace", fontSize: 14, boxShadow: "none", flex: "1 1 auto", minWidth: 0 }}
                />
                <span style={{ padding: "10px 12px", background: "#f8f9fa", color: "#666", borderLeft: "1px solid #e9ecef", userSelect: "none" }}>
                  .{defaultDomain}
                </span>
              </div>

              {/* Slug validation status */}
              <div className="mt-2" style={{ fontSize: 12, minHeight: 18 }}>
                {slugChecking ? (
                  <span className="text-muted">
                    <span className="spinner-border spinner-border-sm me-1" style={{ width: ".6rem", height: ".6rem" }} />
                    Verificando disponibilidad...
                  </span>
                ) : slugOk && slugMsg ? (
                  <span className="text-success">
                    <i className="mdi mdi-check-circle me-1" />{slugMsg}
                  </span>
                ) : slug && !slugOk && slugMsg ? (
                  <span className="text-danger">
                    <i className="mdi mdi-alert-circle me-1" />{slugMsg}
                  </span>
                ) : (
                  <span className="text-muted">Solo letras, n\u00fameros y guiones. M\u00ednimo 3 caracteres.</span>
                )}
              </div>
            </div>

            <div className="p-2 rounded mb-3" style={{ background: "#eff6ff", border: "1px solid #bfdbfe", fontSize: 12, color: "#1e3a8a" }}>
              <i className="mdi mdi-shield-check me-1" />
              SSL autom\u00e1tico incluido. No requiere configuraci\u00f3n adicional.
            </div>

            <Button
              color="success"
              className="w-100"
              onClick={handlePublish}
              disabled={!canPublishFree || !slugOk || slugChecking}
            >
              {publishing ? (
                <><span className="spinner-border spinner-border-sm me-1" />Publicando...</>
              ) : isPublished ? (
                <><i className="mdi mdi-refresh me-1" />Actualizar en {freeUrl.replace("https://", "")}</>
              ) : (
                <><i className="mdi mdi-earth me-1" />Publicar en {freeUrl.replace("https://", "")}</>
              )}
            </Button>
          </TabPane>

          {/* ── Tab: Custom domain ── */}
          <TabPane tabId="custom">
            {/* ── PASO 1 — Configurar DNS ── */}
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 24, height: 24, borderRadius: "50%",
                  background: "#6C63FF", color: "#fff", fontSize: 12, fontWeight: 700,
                }}>1</span>
                <strong style={{ fontSize: 14 }}>Configura tu DNS</strong>
              </div>

              <div className="p-3 rounded" style={{ background: "#eff6ff", border: "1px solid #bfdbfe", fontSize: 13 }}>
                <p className="mb-2" style={{ fontSize: 13 }}>
                  En tu proveedor de DNS (GoDaddy, Cloudflare, Namecheap, etc.) crea este registro:
                </p>
                <table style={{ width: "100%", fontSize: 12, fontFamily: "monospace", borderCollapse: "collapse", background: "#fff", borderRadius: 4 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #bfdbfe" }}>
                      <th style={{ padding: "6px 10px", textAlign: "left" }}>Tipo</th>
                      <th style={{ padding: "6px 10px", textAlign: "left" }}>Host</th>
                      <th style={{ padding: "6px 10px", textAlign: "left" }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: "6px 10px", fontWeight: 700 }}>CNAME</td>
                      <td style={{ padding: "6px 10px" }}>@ o subdominio</td>
                      <td style={{ padding: "6px 10px", fontWeight: 700, color: "#6C63FF" }}>{CNAME_TARGET}</td>
                    </tr>
                  </tbody>
                </table>
                <small className="text-muted d-block mt-2">
                  <strong>Ejemplo:</strong> para <code>landing.miempresa.com</code>, crea un CNAME con host <code>landing</code> apuntando a <code>{CNAME_TARGET}</code>.
                </small>
                <small className="text-muted d-block mt-1">
                  <i className="mdi mdi-alert-circle-outline me-1" />
                  Algunos proveedores no permiten CNAME en el dominio raíz (@). Usa un subdominio en ese caso.
                </small>
              </div>
            </div>

            {/* ── PASO 2 — Verificar DNS ── */}
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 24, height: 24, borderRadius: "50%",
                  background: dnsOk ? "#16a34a" : "#6C63FF", color: "#fff", fontSize: 12, fontWeight: 700,
                }}>
                  {dnsOk ? <i className="mdi mdi-check" /> : "2"}
                </span>
                <strong style={{ fontSize: 14 }}>Verifica tu dominio</strong>
              </div>

              <FormGroup className="mb-2">
                <Input
                  value={customDomain}
                  onChange={(e) => { setCustomDomain(e.target.value); setDnsOk(false); setDnsMsg(""); }}
                  placeholder="landing.miempresa.com"
                  style={{ fontFamily: "monospace", fontSize: 14 }}
                />
              </FormGroup>

              <Button
                color={dnsOk ? "success" : "primary"}
                outline={!dnsOk}
                onClick={handleCheckDns}
                disabled={checking || !customDomain.trim()}
                size="sm"
              >
                {checking ? (
                  <><span className="spinner-border spinner-border-sm me-1" style={{ width: ".7rem", height: ".7rem" }} />Verificando...</>
                ) : dnsOk ? (
                  <><i className="mdi mdi-check-circle me-1" />DNS verificado</>
                ) : (
                  <><i className="mdi mdi-dns me-1" />Verificar DNS</>
                )}
              </Button>

              {dnsMsg && (
                <Alert color={dnsOk ? "success" : "warning"} className="py-2 mt-2 mb-0" style={{ fontSize: 13 }}>
                  <i className={`mdi ${dnsOk ? "mdi-check-circle" : "mdi-alert-circle"} me-1`} />
                  {dnsMsg}
                </Alert>
              )}

              <small className="text-muted d-block mt-2">
                <i className="mdi mdi-clock-outline me-1" />
                La propagación DNS puede tardar de unos minutos hasta 48 horas. Si ya configuraste tu DNS y aún no se refleja, espera y vuelve a intentar.
              </small>
            </div>

            {/* ── PASO 3 — Publicar ── */}
            <div>
              <div className="d-flex align-items-center gap-2 mb-2">
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 24, height: 24, borderRadius: "50%",
                  background: customDomain.trim() ? "#6C63FF" : "#ced4da", color: "#fff", fontSize: 12, fontWeight: 700,
                }}>3</span>
                <strong style={{ fontSize: 14 }}>Publicar en tu dominio</strong>
              </div>

              {!dnsOk && customDomain.trim() && (
                <Alert color="info" className="py-2 mb-2" style={{ fontSize: 13 }}>
                  <i className="mdi mdi-information-outline me-1" />
                  Puedes publicar ahora aunque el DNS aún no se refleje. La landing estará disponible en cuanto la propagación termine (puede tardar hasta 48h).
                </Alert>
              )}

              <Button
                color="success"
                onClick={handlePublish}
                disabled={publishing || !customDomain.trim()}
                className="w-100"
              >
                {publishing ? (
                  <><span className="spinner-border spinner-border-sm me-1" />Publicando...</>
                ) : isPublished ? (
                  <><i className="mdi mdi-refresh me-1" />Re-publicar en {customDomain.trim() || "dominio propio"}</>
                ) : (
                  <><i className="mdi mdi-earth me-1" />Publicar en {customDomain.trim() || "dominio propio"}</>
                )}
              </Button>
            </div>
          </TabPane>
        </TabContent>
      </ModalBody>
    </Modal>
  );
};

export default PublishModal;
